<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
//Components
import DroneSpinner from 'Common/components/elements/feedback/DroneSpinner.vue'
import ServerStatusViewHandler from 'Common/components/templates/ServerStatusViewHandler.vue'

const layout = computed(() =>
  defineAsyncComponent(() => import(`Modules/pages/reportDashboard/ReportSummaryPdfLayout.vue`))
)

const handleAfterPrint = () => {
  window.close()
}
onMounted(() => {
  window.addEventListener('afterprint', handleAfterPrint)
})
onUnmounted(() => {
  window.removeEventListener('afterprint', handleAfterPrint)
})

import { storeToRefs } from 'pinia'
// Utils
import { summarizeDefects } from 'Modules/pages/reportDashboard/utils/summaryGenerator'
//API
import { useSummaryDataQuery } from 'API/queries/iris/report-summary'
import { useGetAirCraftsFiltered } from 'API/queries/iris/aircraft'
import { useGetInspectionInstancesFiltered } from 'API/queries/iris/inspection-instance'
//Store
import { useFilterStore } from 'Store/filterStore'
// Types
import type { ImagesType } from 'Modules/types/generic/image/ImagesType'
import { ImageReviewStates } from 'Modules/types/generic/image/ImageReviewStates'
import { RequestStatus } from 'API/enums'
import { ImageDetailType } from 'Modules/types/generic/image/imageDetailType'

import ReportSummaryDefectMeasurements from 'Modules/pages/reportDashboard/ReportSummaryDefectMeasurements.vue'
import ReportSummaryPdfInspectionDetail from 'Modules/pages/reportDashboard/ReportSummaryPdfInspectionDetail.vue'
import ReportSummaryFindings from 'Modules/pages/reportDashboard/ReportSummaryFindings.vue'
import ReportSummaryImages from 'Modules/pages/reportDashboard/ReportSummaryImages.vue'

//State
const filterStore = useFilterStore()
const { queryParamObj, areFiltersReady } = storeToRefs(filterStore)

// Data
const { data: summaryData, status: summaryStatus } = useSummaryDataQuery({
  variables: { filterQueryParams: queryParamObj },
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
})

const { data: aircraftData, status: aircraftQueryStatus } = useGetAirCraftsFiltered({
  variables: { filterQueryParams: queryParamObj },
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
})

const { data: inspectionData, status: inspectionQueryStatus } = useGetInspectionInstancesFiltered({
  variables: {
    filterQueryParams: {
      aircraft: queryParamObj.value?.aircraft,
      inspectionInstance: queryParamObj.value?.inspectionInstance, // TODO TEST
    },
  },
  enabled: areFiltersReady,
  refetchOnWindowFocus: false,
})

const imagesLoaded = ref('loading')
// TODO: break out into common util
const summaryImageDetails = computed<ImagesType>(() => {
  if (summaryStatus.value !== RequestStatus.success) return {}

  // separate images by complete / incomplete
  return summaryData.value.images.reduce(
    (acc: ImagesType, i: ImageDetailType) => {
      if (i.reviewState === ImageReviewStates.Complete) {
        acc.complete.push(i)
      } else {
        acc.incomplete.push(i)
      }
      return acc
    },
    { incomplete: [], complete: [] }
  )
})
// TODO: break out into common util
const measurementsSummaryData = computed<Array<ImagesType>>(() => {
  if (summaryStatus.value !== RequestStatus.success) return []
  return summarizeDefects(summaryData.value.annotations)
})
// TODO: break out into common util
const summaryAnnotationDetails = computed<ImagesType>(() => {
  if (summaryStatus.value !== RequestStatus.success) return []
  return summaryData.value.annotations
})

watch([summaryStatus, aircraftQueryStatus, inspectionQueryStatus, imagesLoaded], async (newStatuses) => {
  if (newStatuses.every((status) => status === 'success')) {
    await nextTick()
    window.print()
  }
})
</script>

<template>
  <div class="print-page p-3">
    <div class="print:hidden p-10">
      <h3 class="text-xl">Generating Report...</h3>
      <div class="h-full w-full flex items-center justify-center">
        <DroneSpinner />
      </div>
    </div>
    <div class="hidden-visual print:block">
      <ServerStatusViewHandler :statuses="[summaryStatus, aircraftQueryStatus, inspectionQueryStatus]">
        <template #loading>
          <div class="print:hidden h-full w-full pb-10 flex items-center justify-center">
            <DroneSpinner />
          </div>
        </template>
        <template #error> ...error </template>
        <template #success>
          <component :is="layout">
            <template #inspection-detail>
              <ReportSummaryPdfInspectionDetail :aircraft-data="aircraftData" :inspection-data="inspectionData" />
            </template>
            <template #defect-messurements>
              <div class="print-width">
                <ReportSummaryDefectMeasurements :measurements-summary-data="measurementsSummaryData" />
              </div>
            </template>
            <template #annotation-section>
              <div class="print-width">
                <div class="h-8 flex items-center text-slate-600 font-semibold mt-2 mb-4">Annotations</div>
                <ReportSummaryFindings :annotations="summaryAnnotationDetails" />
                <hr />
                <ReportSummaryImages :images="summaryImageDetails" :inspection-instance="queryParamObj.inspectionInstance" @on-images-loaded="imagesLoaded = 'success'" />
              </div>
              <div class="mt-5">
                <el-alert
                  type="info"
                  show-icon
                  :closable="false"
                  title="Experimental Feature"
                  description="The measurements displayed are part of an experimental feature. While we strive for accuracy, the values shown may not be 100% precise. Please consider this when using these measurements for your work."
                >
                </el-alert>
              </div>
            </template>
          </component>
        </template>
      </ServerStatusViewHandler>
    </div>
  </div>
</template>

<style>
@media print {
  /* required overrides for print page */
  .print-page {
    -webkit-print-color-adjust: exact !important;
  }
  #app {
    height: auto !important;
    max-height: none !important;
  }
  #app main {
    height: auto !important;
    max-height: none !important;
  }
  #base-layout #page-filter {
    height: auto !important;
    max-height: none !important;
    display: none !important;
  }
  #base-layout #header-actions {
    height: auto !important;
    max-height: none !important;
    display: none !important;
  }

  #base-layout .el-input,
  #base-layout .caret-wrapper,
  #basy-layout .el-icon.el-collapse-item__arrow {
    height: auto !important;
    max-height: none !important;
    display: none !important;
  }

  #base-layout table.el-table__body tr.el-table__row.discpepancy-findings,
  #base-layout table.el-table__body tr.el-table__row.discpepancy-findings .el-table__cell,
  #base-layout .el-table__row.discpepancy-findings .cell {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  #base-layout .hidden-visual {
    visibility: visible !important;
  }
}

#base-layout .print-width {
  width: 720px;
}
#base-layout .hidden-visual {
  visibility: hidden;
}
</style>
